import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TableComponentContentfulModel } from 'src/api/models/ComparisonTable';
import Image from 'src/elements/image/image';
import Heading from 'src/elements/typography/heading';
import Paragraph from 'src/elements/typography/paragraph';
import useWindowResize from 'src/hooks/useWindowResize';
import CheckedItem from 'src/molecules/checklist/checked.item';
import { palette } from 'src/styles/colors';
import {
  ComparisonTableContent,
  ComparisonTableHeadCell,
  ComparisonTableRowCell,
  ComparisonTableBody,
  ComparisonTableCellContent,
  ComparisonTableContainer,
  ComparisonTableRow
} from './ComparisonTable.styles';
import getDynamicContentfulComponent from 'src/functions/dynamicComponentFactory';

interface ComparisonTableProps {
  head: TableComponentContentfulModel;
  rows: TableComponentContentfulModel[];
  hideCheckIconText?: boolean;
  responsiveBreakpoint?: string;
  responsiveCellPadding?: string;
}

const ComparisonTable = ({ head, rows, hideCheckIconText, responsiveBreakpoint, responsiveCellPadding }: ComparisonTableProps): JSX.Element => {
  const { width: windowWidth } = useWindowResize();
  const [ columnSize, setColumnSize ] = useState<number>(220);
  const [ isMobile, setIsMobile ] = useState(false);

  const shouldHaveStickyColumn = useMemo(() => head.content.length > 3, [ head.content ]);

  useEffect(() => {
    if (shouldHaveStickyColumn) {
      if (windowWidth <= 500) {
        setColumnSize(100);
      } else if (windowWidth <= 700) {
        setColumnSize(160);
      } else {
        setColumnSize(220);
      }
    }
  }, [ windowWidth, shouldHaveStickyColumn ])

  useEffect(() => setIsMobile(windowWidth <= 800), [ windowWidth ])

  const columnWidth = useMemo(
    () => (!shouldHaveStickyColumn ? `${Math.floor(100 / head.content.length)}%` : `${columnSize}px`),
    [ head.content, columnSize, shouldHaveStickyColumn ]
  );

  const shouldRenderCheckIcon = useCallback((cellContent: string, checkIcon: 'Checked' | 'Not Checked' | 'None') => {
    if (!checkIcon || checkIcon === 'None') {
      return false;
    }

    // if we're in a mobile view, the text should be hidden and the X will be rendered
    // on desktop, the X will be hidden and the text will be rendered
    return !(cellContent && checkIcon === 'Not Checked') || isMobile
  }, [ isMobile ]);

  return (
    <ComparisonTableContainer sticky={shouldHaveStickyColumn}>
      <ComparisonTableContent fixedWidth={!shouldHaveStickyColumn ? null : `${Math.floor(head.content.length * columnSize)}px`}>
        <thead>
          <tr>
            {
              head.content.map((cell, index) => (
                <ComparisonTableHeadCell
                  key={index}
                  background={cell.backgroundColor ?? palette.white}
                  width={columnWidth}
                  sticky={shouldHaveStickyColumn ? index < 2 : false}
                >
                  {
                    cell.contentfulchildren ? (
                      <React.Fragment>
                        {getDynamicContentfulComponent(cell)}
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        {
                          cell.content && !cell.image && <Heading text={cell.content} size="1.5rem" sizeSubLg="0.9rem" align="center" />
                        }
                        {
                          cell.image && <Image childImageSharp={cell.image?.gatsbyImageData} alt={cell.content} />
                        }
                      </React.Fragment>
                    )
                  }
                </ComparisonTableHeadCell>
              ))
            }
          </tr>
        </thead>
        <ComparisonTableBody>
          {
            rows.map((row, index) => (
              <ComparisonTableRow key={index} borderWidth={row.borderWidth}>
                {
                  row.content.map((cell, cellIndex) => (
                    <ComparisonTableRowCell
                      key={cellIndex}
                      background={cell.backgroundColor ?? palette.white}
                      width={columnWidth}
                      hideCheckIconText={hideCheckIconText && cellIndex !== 0}
                      sticky={shouldHaveStickyColumn ? cellIndex < 2 : false}
                      responsiveBreakpoint={responsiveBreakpoint}
                      responsiveCellPadding={responsiveCellPadding}
                    >
                      {
                        cell.contentfulchildren ? (
                          <React.Fragment>
                            {getDynamicContentfulComponent(cell)}
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            {
                              cell.image ?
                                <Image childImageSharp={cell.image.gatsbyImageData} alt={cell.content} /> :
                                (
                                  <React.Fragment>
                                    {
                                      shouldRenderCheckIcon(cell.content, cell.checkIcon) && (
                                        <CheckedItem
                                          checked={cell.checkIcon === 'Checked'}
                                          text={!hideCheckIconText ? cell.checkIcon === 'Checked' ? 'Yes' : 'No' : null}
                                          fontWeight={cell.contentFontWeight}
                                        />
                                      )
                                    }
                                    {
                                      cell.content && (
                                        <ComparisonTableCellContent breakpoint={cell.hideContentBreakpoint}>
                                          <Paragraph text={cell.content} size="1.3rem" align="left" weight={cell.contentFontWeight ?? 'light'}/>
                                        </ComparisonTableCellContent>
                                      )
                                    }
                                  </React.Fragment>
                                )
                            }
                          </React.Fragment>
                        )
                      }
                    </ComparisonTableRowCell>
                  ))
                }
              </ComparisonTableRow>
            ))
          }
        </ComparisonTableBody>
      </ComparisonTableContent>
    </ComparisonTableContainer>
  )
}

export default ComparisonTable;
